import { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { ServiceType } from '../../types';
import { SERVICES } from '../../constants';
import Card from '../Card';

interface Step1Props {
  selectedService: ServiceType | null;
  onSelectService: (service: ServiceType) => void;
}

export default function Step1ServiceSelection({ selectedService, onSelectService }: Step1Props) {
  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xl)',
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto'
  };

  const headerStyles: CSSProperties = {
    textAlign: 'center',
    marginBottom: 'var(--spacing-lg)'
  };

  const titleStyles: CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    marginBottom: 'var(--spacing-sm)'
  };

  const subtitleStyles: CSSProperties = {
    fontSize: '1.125rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6
  };

  const gridStyles: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 'var(--spacing-lg)',
    width: '100%'
  };

  const cardContentStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
    height: '100%'
  };

  const serviceNameStyles: CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--color-secondary)'
  };

  const descriptionStyles: CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
    flex: 1
  };

  const durationStyles: CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)'
  };

  return (
    <div style={containerStyles}>
      <motion.div
        style={headerStyles}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 style={titleStyles}>Select Your Meeting Type</h1>
        <p style={subtitleStyles}>
          Choose the service that best fits your needs
        </p>
      </motion.div>

      <div style={gridStyles}>
        {SERVICES.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card
              onClick={() => onSelectService(service.id)}
              selected={selectedService === service.id}
            >
              <div style={cardContentStyles}>
                <h3 style={serviceNameStyles}>{service.name}</h3>
                <p style={descriptionStyles}>{service.description}</p>
                <div style={durationStyles}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 4V8L11 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  {service.duration}
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
